import React, { useEffect, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Landmark, Receipt, ShieldAlert, TrendingUp, FileWarning, Loader2, Globe } from 'lucide-react';

export default function TaxPolicies({ region: regionProp }) {
  const [region, setRegion] = useState(regionProp || 'us');
  const [regions, setRegions] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    ConstructionAPI.getRegions().then(r => setRegions(r.regions || []));
  }, []);

  useEffect(() => {
    if (regionProp && regionProp !== region) setRegion(regionProp);
    // eslint-disable-next-line
  }, [regionProp]);

  useEffect(() => {
    setLoading(true);
    ConstructionAPI.getTaxPolicy(region)
      .then(r => setPolicy(r))
      .finally(() => setLoading(false));
  }, [region]);

  const fmtPct = (n) => n != null ? `${Number(n).toFixed(n % 1 === 0 ? 0 : 2)}%` : '—';

  return (
    <div className="space-y-4" data-testid="tax-policies-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-600 rounded-lg"><Landmark className="h-5 w-5 text-white" /></div>
              <div>
                <CardTitle className="text-base">Taxes, Contingencies &amp; Financial Policies</CardTitle>
                <p className="text-xs text-slate-500">Indicative regional snapshot — review with local advisor before tendering.</p>
              </div>
            </div>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="h-9 w-56" data-testid="tax-region-select"><SelectValue /></SelectTrigger>
              <SelectContent>{regions.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <div className="p-6 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 inline animate-spin mr-1.5" />Loading…</div>}
          {policy && !loading && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tax-stat-grid">
                <Stat icon={<Receipt className="h-4 w-4" />} accent="bg-rose-600" label={policy.vat_label || 'VAT / GST'} value={fmtPct(policy.vat_gst_pct)} />
                <Stat icon={<Receipt className="h-4 w-4" />} accent="bg-orange-600" label="Corporate tax" value={fmtPct(policy.corporate_tax_pct)} />
                <Stat icon={<Globe className="h-4 w-4" />} accent="bg-amber-600" label="Withholding tax" value={fmtPct(policy.withholding_pct)} />
                <Stat icon={<Globe className="h-4 w-4" />} accent="bg-yellow-600" label="Import duty (avg)" value={fmtPct(policy.import_duty_pct)} />
                <Stat icon={<ShieldAlert className="h-4 w-4" />} accent="bg-indigo-600" label="Social charges (employer)" value={fmtPct(policy.social_charges_pct)} />
                <Stat icon={<TrendingUp className="h-4 w-4" />} accent="bg-emerald-700" label="Typical contingency" value={fmtPct(policy.typical_contingency_pct)} />
                <Stat icon={<TrendingUp className="h-4 w-4" />} accent="bg-sky-600" label="Bench interest rate" value={fmtPct(policy.interest_rate_pct)} />
                <Stat icon={<Landmark className="h-4 w-4" />} accent="bg-slate-900" label="Region" value={policy.label || region} />
              </div>

              {policy.policy_notes?.length > 0 && (
                <div className="mt-5">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2 inline-flex items-center gap-1.5">
                    <FileWarning className="h-4 w-4 text-amber-600" />
                    Policy notes &amp; flags
                  </h4>
                  <ul className="space-y-2" data-testid="tax-policy-notes">
                    {policy.policy_notes.map((n, i) => (
                      <li key={i} className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-start gap-2">
                        <Badge variant="outline" className="text-[10px] mt-0.5 shrink-0">#{i + 1}</Badge>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                <strong className="text-slate-800">Disclaimer:</strong> Values shown are indicative aggregates for {policy.label || region} and may differ by city, project type and contract structure. Always validate with a qualified tax advisor and the latest local statutes before committing to a tender.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}
